import 'react-responsive-carousel/lib/styles/carousel.min.css';
import * as apiValues from '../constants/apiValues';
import * as helpers from '../helpers';
import * as mCarouselActions from '../actions/mCarouselActions';
import CarouselSlide from './CarouselSlide';
import PropTypes from 'prop-types';
import React from 'react';
import { bindActionCreators } from 'redux';
import { Carousel } from 'react-responsive-carousel';
import { connect } from 'react-redux';
import { withRouter } from '../withRouterCompat';


class MCarousel extends React.Component{
  constructor(props){
    super(props);
    const prefersReducedMotion = typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.state = {
      prefersReducedMotion,
      videoPaused: false,
    };
    this.videoRef = React.createRef();
    this.reducedMotionQuery = null;
  }

  componentDidMount(){
    //console.log(`MCarousel.componentDidMount()`, this.props);

    this.props.actions.initMCarousel();

    if(typeof window !== 'undefined' && window.matchMedia){
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if(this.reducedMotionQuery.matches !== this.state.prefersReducedMotion){
        this.setState({prefersReducedMotion: this.reducedMotionQuery.matches});
      }
      if(this.reducedMotionQuery.addEventListener){
        this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
      } else {
        this.reducedMotionQuery.addListener(this.onReducedMotionChange);
      }
    }

    this.scheduleCarouselAccessibilitySync();
  }

  componentWillUnmount(){
    if(this.reducedMotionQuery){
      if(this.reducedMotionQuery.removeEventListener){
        this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
      } else {
        this.reducedMotionQuery.removeListener(this.onReducedMotionChange);
      }
    }
  }

  componentDidUpdate(){
    this.scheduleCarouselAccessibilitySync();
  }

  scheduleCarouselAccessibilitySync(){
    if(typeof window === 'undefined') return;

    const sync = () => this.syncCarouselFocusOrder();
    window.requestAnimationFrame(() => {
      sync();
      window.requestAnimationFrame(sync);
    });
    // infiniteLoop clones mount after the first paint
    window.setTimeout(sync, 0);
  }

  getActiveCarouselSlide(root){
    const slides = Array.from(root.querySelectorAll('.carousel .slide'));
    if(slides.length === 0) return null;

    const selected = slides.filter((slide) => slide.classList.contains('selected'));
    const candidates = selected.length > 0 ? selected : slides;
    const track = root.querySelector('.carousel .slider') || root.querySelector('.slider-wrapper');
    const trackRect = track?.getBoundingClientRect();

    if(!trackRect){
      return candidates[0];
    }

    let bestSlide = null;
    let bestDistance = Infinity;

    candidates.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      if(rect.right < trackRect.left - 8 || rect.left > trackRect.right + 8){
        return;
      }

      const slideCenter = rect.left + (rect.width / 2);
      const trackCenter = trackRect.left + (trackRect.width / 2);
      const distance = Math.abs(slideCenter - trackCenter);

      if(distance < bestDistance){
        bestDistance = distance;
        bestSlide = slide;
      }
    });

    return bestSlide || candidates[0];
  }

  /**
   * Library default order is dots, prev, slides, next (tabs hit pills before hero search).
   * Desired tab order: pause video (first .m-carousel child), slides (hero search), pills, prev, next.
   */
  relocateCarouselTabOrder(root){
    const carousel = root.querySelector('.carousel.carousel-slider');
    if(!carousel){
      return;
    }

    const wrapper = carousel.querySelector('.slider-wrapper');
    const dots = carousel.querySelector('.control-dots');
    const prev = carousel.querySelector('.control-arrow.control-prev');
    const next = carousel.querySelector('.control-arrow.control-next');
    if(!wrapper){
      return;
    }

    [wrapper, dots, prev, next].filter(Boolean).forEach((node) => {
      carousel.appendChild(node);
    });
  }

  syncCarouselHeadingLevels(root){
    const activeSlide = this.getActiveCarouselSlide(root);
    const descriptions = root.querySelectorAll('.carousel .slide .carouselslide__description');
    let promotedH1 = false;

    descriptions.forEach((desc) => {
      const slide = desc.closest('.slide');
      if(!slide) return;

      const isHero = Boolean(slide.querySelector('.carouselslide__search'));
      const isActive = activeSlide ? slide === activeSlide : slide.classList.contains('selected');
      const shouldBeH1 = isHero && isActive && !promotedH1;
      const targetTag = shouldBeH1 ? 'h1' : 'h2';

      if(shouldBeH1){
        promotedH1 = true;
      }

      if(desc.tagName !== targetTag){
        const replacement = document.createElement(targetTag);
        replacement.className = desc.className;
        replacement.innerHTML = desc.innerHTML;
        desc.replaceWith(replacement);
      }
    });
  }

  syncCarouselFocusOrder(){
    if(typeof document === 'undefined') return;
    const root = document.querySelector('.m-carousel');
    if(!root) return;

    this.relocateCarouselTabOrder(root);

    root.querySelectorAll('.control-arrow.control-prev').forEach((el) => {
      el.setAttribute('aria-label', 'Previous slide');
      if(el.getAttribute('tabindex') === '-1'){
        el.removeAttribute('tabindex');
      }
    });
    root.querySelectorAll('.control-arrow.control-next').forEach((el) => {
      el.setAttribute('aria-label', 'Next slide');
      if(el.getAttribute('tabindex') === '-1'){
        el.removeAttribute('tabindex');
      }
    });

    const activeSlide = this.getActiveCarouselSlide(root);

    root.querySelectorAll('.carousel .slide').forEach((slide) => {
      const isActive = activeSlide ? slide === activeSlide : slide.classList.contains('selected');

      // Hide offscreen slides (including infiniteLoop clones) from assistive tech (LLSU2-148).
      if(isActive){
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }

      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])').forEach((el) => {
        if(isActive){
          if(el.getAttribute('data-ls-carousel-tabindex') === '-1'){
            el.removeAttribute('tabindex');
            el.removeAttribute('data-ls-carousel-tabindex');
          }
        } else if(!el.hasAttribute('data-ls-carousel-tabindex')){
          el.setAttribute('data-ls-carousel-tabindex', el.getAttribute('tabindex') || '');
          el.setAttribute('tabindex', '-1');
        }
      });
    });

    this.syncCarouselHeadingLevels(root);
  }

  onCarouselChange = () => {
    window.requestAnimationFrame(() => {
      this.syncCarouselFocusOrder();
    });
  };

  onReducedMotionChange = (event) => {
    this.setState({
      prefersReducedMotion: event.matches,
      videoPaused: false,
    });
  };

  toggleVideo = () => {
    const video = this.videoRef.current;
    if(!video){
      return;
    }

    if(video.paused){
      video.play();
      this.setState({videoPaused: false});
    } else {
      video.pause();
      this.setState({videoPaused: true});
    }
  };

  renderCarouselIndicator = (onClickHandler, isSelected, index) => {
    const slideNumber = index + 1;
    return (
      <li key={index}
          className={isSelected ? 'dot selected' : 'dot'}
          role="presentation">
        <button type="button"
                className="m-carousel__dot-button"
                onClick={onClickHandler}
                aria-label={`Go to slide ${slideNumber}`}
                aria-current={isSelected ? 'true' : undefined}/>
      </li>
    );
  };

  renderBackgroundImage(imageUrl){
    return (
      <div className="m-carousel__background-img"
           aria-hidden="true"
           style={{backgroundImage: `url(${apiValues.imgPath(imageUrl)})`}}/>
    );
  }

  renderVideoControl(videoUrl){
    const {prefersReducedMotion, videoPaused} = this.state;
    if(!videoUrl || prefersReducedMotion){
      return null;
    }

    return (
      <button type="button"
              className="visually-hidden-focusable m-carousel__video-control"
              aria-pressed={videoPaused}
              aria-controls="hero-video"
              onClick={this.toggleVideo}>
        {videoPaused ? 'Play' : 'Pause'} background video
      </button>
    );
  }

  renderBackground(){
    const video = this.props.mCarousel.backgroundVideo;
    const image = this.props.mCarousel.backgroundImage;
    const videoUrl = video && (video.url || video.Url);
    const imageUrl = image && (image.url || image.Url);
    const {prefersReducedMotion} = this.state;

    if(prefersReducedMotion && imageUrl){
      return this.renderBackgroundImage(imageUrl);
    }

    if(videoUrl && !prefersReducedMotion){
      return (
        <div className="m-carousel__background-video-container">
          <video id="hero-video"
                 ref={this.videoRef}
                 className="m-carousel__background-video"
                 src={apiValues.imgPath(videoUrl)}
                 autoPlay
                 loop
                 muted
                 playsInline
                 preload="auto"
                 aria-hidden="true"
                 tabIndex={-1}/>
          {imageUrl ? this.renderBackgroundImage(imageUrl) : null}
        </div>
      );
    }

    if(imageUrl){
      return this.renderBackgroundImage(imageUrl);
    }

    return null;
  }
  renderCarousel(){
    if(!this.props.mCarousel.carouselSlides) return null;

    return (
      <div className="m-carousel__carousel-container">
        <Carousel showStatus={false}
                  showThumbs={false}
                  infiniteLoop={true}
                  selectedItem={this.props.selectedItem}
                  onChange={this.onCarouselChange}
                  labels={{
                    leftArrow: 'Previous slide',
                    rightArrow: 'Next slide',
                    item: 'Go to slide',
                  }}
                  renderIndicator={this.renderCarouselIndicator}>
          {this.props.mCarousel.carouselSlides.map(carouselSlideId => {
            let carouselSlide = helpers.getById(this.props.carouselSlide, carouselSlideId);

            return carouselSlide ? (
              <CarouselSlide className="m-carousel__carouselslide" key={carouselSlide.id} cmsId={carouselSlide.id}>
                <img alt="" src=""/>
              </CarouselSlide>
            ) : null;
          })}
        </Carousel>
      </div>
    );
  }
  render(){
    // console.log(`MCarousel render`, this.props);
    if(!this.props.mCarousel ||
       !this.props.mCarousel.carouselSlides ||
       this.props.mCarousel.carouselSlides.length < 1) return null;

    const video = this.props.mCarousel.backgroundVideo;
    const videoUrl = video && (video.url || video.Url);

    return (
      <div className={`m-carousel ${this.props.className || ''}`}>
        {this.renderVideoControl(videoUrl)}
        <div className="container container--coupling">
          <div className="row">
            {this.renderCarousel()}
            {this.renderBackground()}
          </div>
        </div>
      </div>
    );
  }
}


export default withRouter(connect(
  (state, ownProps) => ({
    carouselSlide: state.carouselSlide,
    mCarousel: ownProps.cmsId
      ? helpers.getById(state.mCarousel, ownProps.cmsId)
      : helpers.getLoadedModule(state.mCarousel),
  }),
  (dispatch) => ({
    actions: bindActionCreators(mCarouselActions, dispatch)
  })
)(MCarousel));


MCarousel.propTypes = {
  actions: PropTypes.object,
  carouselSlide: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  cmsId: PropTypes.string,
  mCarousel: PropTypes.object,
  selectedItem: PropTypes.number,
};
